/**
 * Copyright © 2025-2026 Cognizant Digital Business, Evolutionary AI.
 * All Rights Reserved.
 * Issued under the Academic Public License.
 *
 * You can be released from the terms, and requirements of the Academic Public
 * License by purchasing a commercial license.
 * Purchase of a commercial license is mandatory for any use of the
 * LMX Software in commercial settings.
 */
// END COPYRIGHT
/**
 * API service for communicating with the backend server.
 *
 * Client-held state architecture:
 * - The client maintains the full app_state in memory (via useAppState hook)
 * - Every mutating request sends the current app_state to the server
 * - The server processes the request, updates the state, and returns it
 * - The server does NOT persist state between requests
 * - This makes the server stateless and horizontally scalable
 */
import { API_BASE_URL } from "./apiConfig";
import { getByokLlmConfig } from "./byok/byokKeys";
import logger from "./logger";
export const fetchRuntimeConfig = async () => {
    const res = await fetch(`${API_BASE_URL}/api/config`);
    if (!res.ok)
        throw new Error(`Config fetch failed: ${res.status}`);
    return res.json();
};
// Attach the API keys saved in this tab, so the server can pass them to neuro-san.
const withByok = (data) => {
    const llmConfig = getByokLlmConfig();
    return llmConfig ? { ...data, llm_config: llmConfig } : data;
};
const request = async (url, options = {}) => {
    const headers = {
        "Content-Type": "application/json",
    };
    if (options.headers) {
        Object.assign(headers, options.headers);
    }
    const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
    });
    if (!response.ok) {
        let parseErr;
        const errorData = await response.json().catch((err) => {
            parseErr = err;
            return { detail: "Unknown error" };
        });
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`, { cause: parseErr });
    }
    return response.json();
};
const initializeSession = async (data) => request("/api/sessions", {
    method: "POST",
    body: JSON.stringify(data),
});
const getSession = async (sessionId) => request(`/api/sessions/${sessionId}`);
const submitIdea = async (data) => request(`/api/sessions/${data.session_id}/ideas`, {
    method: "POST",
    body: JSON.stringify(withByok(data)),
});
const generateIdea = async (data) => request(`/api/sessions/${data.session_id}/generate-idea`, {
    method: "POST",
    body: JSON.stringify(withByok(data)),
});
const generateIdeas = async (data) => request(`/api/sessions/${data.session_id}/generate-ideas`, {
    method: "POST",
    body: JSON.stringify(withByok(data)),
});
/**
 * Parse a single Server-Sent Events (SSE) data line and notify caller with the parsed update.
 *
 * Expects lines prefixed with the string "data: ". If the payload equals the sentinel
 * string "[DONE]", the function returns true to indicate stream completion.
 *
 * @param line - A single line read from the SSE stream (may include the "data: " prefix).
 * @param operationName - Short name of the operation; used for context in log messages.
 * @param onUpdate - Callback invoked with the parsed `GenerateIdeasResponse` or `ApiErrorResponse`.
 * @returns True when the "[DONE]" sentinel was received (stream completed), otherwise false.
 */
const processSSEDataLine = (line, operationName, onUpdate) => {
    const DATA_PREFIX = "data: ";
    // SSE lines must start with "data: " prefix
    if (!line.startsWith(DATA_PREFIX)) {
        return false;
    }
    const eventData = line.slice(DATA_PREFIX.length);
    if (eventData === "[DONE]") {
        return true;
    }
    try {
        const update = JSON.parse(eventData);
        onUpdate(update);
    }
    catch (error) {
        logger.error(`Failed to parse ${operationName} SSE data: %o %o`, eventData, error);
        const parseErrorMsg = error instanceof Error ? error.message : `Failed to parse SSE data: ${String(error)}`;
        // Notify caller about the parsing failure so the client can handle it instead of silently ignoring.
        try {
            onUpdate({ success: false, error: parseErrorMsg });
        }
        catch (notifyErr) {
            logger.error(`Failed to notify caller about ${operationName} parse error: %o`, notifyErr);
        }
    }
    return false;
};
/**
 * Helper to parse SSE stream and invoke callback for each update.
 */
const parseSSEStream = async (response, onUpdate, operationName) => {
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("text/event-stream")) {
        logger.warn(`Server returned non-SSE response for ${operationName}. Content-Type: %s`, contentType);
        const jsonData = await response.json();
        // For non-SSE fallback, send a single update and return to signal completion
        // (equivalent to receiving data followed by [DONE] in SSE flow)
        onUpdate(jsonData);
        return;
    }
    // `fetch()` returns a ReadableStream. Use `getReader()` to read bytes incrementally.
    // EventSource only supports GET. We need POST to send `app_state`, so parse SSE manually.
    // Fall back to JSON when the server doesn't send `text/event-stream`.
    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("Response body is not readable");
    }
    const decoder = new TextDecoder();
    let buffer = "";
    try {
        // Run the while loop until the stream is done
        while (true) {
            const { done, value } = await reader.read();
            // Stream is done based on the response from reader
            if (done) {
                break;
            }
            // `stream: true` handles multi-byte characters split across chunks
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Preserve incomplete line for next chunk
            for (const line of lines) {
                // Process SSE data lines. Exit early if [DONE] signal received (returns true).
                if (processSSEDataLine(line, operationName, onUpdate)) {
                    return;
                }
            }
        }
    }
    finally {
        // Clean up the stream on all exit paths (successful completion, early [DONE] termination, or error). Errors
        // propagate as rejected promises automatically and will cause a fallback to batch mode.
        reader.cancel();
    }
};
const generateIdeasStreaming = async (data, onUpdate) => {
    const url = `${API_BASE_URL}/api/sessions/${data.session_id}/generate-ideas?stream=true`;
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withByok(data)),
    });
    if (!response.ok) {
        let parseErr;
        const errorData = await response.json().catch((err) => {
            parseErr = err;
            return { detail: "Unknown error" };
        });
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`, { cause: parseErr });
    }
    await parseSSEStream(response, onUpdate, "generateIdeas");
};
const calibrateSession = async (sessionId, appState) => request(`/api/sessions/${sessionId}/calibrate`, {
    method: "POST",
    body: JSON.stringify(withByok({ app_state: appState })),
});
const calibrateSessionStreaming = async (sessionId, appState, onUpdate) => {
    const url = `${API_BASE_URL}/api/sessions/${sessionId}/calibrate?stream=true`;
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(withByok({ app_state: appState })),
    });
    if (!response.ok) {
        let parseErr;
        const errorData = await response.json().catch((err) => {
            parseErr = err;
            return { detail: "Unknown error" };
        });
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`, { cause: parseErr });
    }
    await parseSSEStream(response, onUpdate, "calibrate");
};
const downloadIdeas = async (sessionId, appState) => request(`/api/sessions/${sessionId}/download-ideas`, {
    method: "POST",
    body: JSON.stringify({ app_state: appState }),
});
export const apiService = {
    initializeSession,
    getSession,
    submitIdea,
    generateIdea,
    generateIdeas,
    generateIdeasStreaming,
    calibrateSession,
    calibrateSessionStreaming,
    downloadIdeas,
};
